

import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { StatsSection, ChartsSection, ListsSection } from './sections';
import { FinancialWidget } from './widgets';
import { formatTime } from '@/lib/dateFormat';
import { useDashboard } from '../useDashboard';

function DashboardSkeleton() {
  return (
    <div className="space-y-6" aria-label="Loading dashboard" aria-busy="true">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-[104px] rounded-xl bg-stone-100 animate-pulse" />)}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        <div className="xl:col-span-2 rounded-xl border border-stone-200 bg-white p-4 sm:p-6 space-y-3">
          <div className="h-4 w-32 rounded bg-stone-100 animate-pulse" />
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-lg bg-stone-100 animate-pulse" />)}
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6 space-y-3">
          <div className="h-4 w-24 rounded bg-stone-100 animate-pulse" />
          {[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-lg bg-stone-100 animate-pulse" />)}
        </div>
      </div>
    </div>
  );
}

export function DashboardGrid() {
  const { data, loading, refreshing, error, refreshError, updatedAt, retry } = useDashboard();

  if (loading && !data) return <DashboardSkeleton />;

  if (!data) {
    return (
      <div className="bg-white border border-stone-200 rounded-xl p-8 sm:p-10 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <p className="font-semibold text-stone-800">Couldn't load the dashboard</p>
        {error && <p className="text-sm text-stone-500 mt-1">{error}</p>}
        <button
          type="button"
          onClick={retry}
          className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50"
        >
          <RefreshCw className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StatsSection data={data} />

      <ListsSection data={data} />

      {data.money && (
        <>
          <FinancialWidget money={data.money} />
          <ChartsSection monthly={data.money.monthly} />
        </>
      )}

      <div className="flex flex-wrap items-center justify-center gap-x-2 text-xs text-stone-500">
        {refreshError ? (
          <span className="text-amber-700">
            Couldn't refresh ({refreshError}). Showing figures from {updatedAt ? formatTime(updatedAt) : 'earlier'}.
          </span>
        ) : (
          <span>Updated {updatedAt ? formatTime(updatedAt) : '—'} · refreshes every minute</span>
        )}
        <button
          type="button"
          onClick={retry}
          disabled={refreshing}
          className="h-10 inline-flex items-center gap-1.5 px-3 rounded-lg text-amber-700 font-semibold hover:bg-amber-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> {refreshError ? 'Try again' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}
